import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import FeedbackError from "./FeedbackError";
import FeedbackFields from "./FeedbackFields";
import FeedbackPanelSwap from "./FeedbackPanelSwap";
import FeedbackThankYou from "./FeedbackThankYou";
import { FeedbackFormDef } from "./forms";
import { FeedbackValues } from "../../../services/feedbackSubmission";

/** What the last submit attempt produced. */
type SubmitResult =
  | { status: "success" }
  | { status: "error"; message: string };

/**
 * Dismissible feedback dialog — backdrop click, ESC and the close button all
 * work. This is the deliberate opposite of FeedbackModal, which gates the app
 * during onboarding and must not be escapable. Both render the same
 * FeedbackFields body.
 *
 * Dismissal is wired up by hand rather than left to Headless UI. HUI 1.7
 * registers its ESC and outside-click handlers on `window`, and inside the
 * Excalidraw canvas keydown never gets that far — it reaches `document` and
 * stops. Relying on HUI's built-ins here yields a dialog that only the X button
 * can close. Handling it locally beats loosening Excalidraw's global key
 * handling, which owns every canvas shortcut.
 */

type Props = {
  isOpen: boolean;
  form: FeedbackFormDef;
  onClose: () => void;
  onSubmit: (values: FeedbackValues) => Promise<void>;
};

const FeedbackDialog: React.FC<Props> = ({
  isOpen,
  form,
  onClose,
  onSubmit,
}) => {
  // Whether the result panel is up, kept separate from *which* result it shows.
  // Retry flips only this one back, so `result` still describes the panel that
  // is fading out; deriving both from one state would swap the error for the
  // thank-you for the frame before the rewind lands.
  const [resultVisible, setResultVisible] = useState(false);
  const [result, setResult] = useState<SubmitResult | null>(null);

  // The answers as last seen. Held here, not in the form, because the form is
  // unmounted both by the panel swap and by closing the dialog — so anything it
  // owned would be gone on the way back. FeedbackFields reports its values as
  // it unmounts, which is what makes Escape, Cancel and the backdrop non-
  // destructive: every exit route preserves the draft, not just a failed
  // submit. Deliberately not cleared on close, so reopening resumes.
  const [draft, setDraft] = useState<FeedbackValues | undefined>(undefined);

  // Whether the press that started this click landed on the overlay itself.
  // A `click` is dispatched on the nearest common ancestor of mousedown and
  // mouseup, so drag-selecting inside a textarea and releasing past the panel
  // edge targets the overlay — the panel's stopPropagation is not on that path.
  // Closing on the click alone therefore destroys a half-typed answer whenever
  // a selection runs off the edge. Requiring the press to have *started* here
  // makes the backdrop dismiss a click, not a drag.
  const pressStartedOnOverlay = useRef(false);

  // Reset on open rather than on close so the result survives the closing
  // transition instead of flashing the form back for a frame.
  useEffect(() => {
    if (isOpen) {
      setResultVisible(false);
    }
  }, [isOpen]);

  // Catches rather than propagating: the outcome is the error panel now, not an
  // inline banner on a form that still looks submittable.
  const handleSubmit = async (values: FeedbackValues) => {
    setDraft(values);
    try {
      await onSubmit(values);
      setResult({ status: "success" });
    } catch (error: any) {
      setResult({
        status: "error",
        message:
          error?.message || "Something went wrong. Please try again shortly.",
      });
    }
    setResultVisible(true);
  };

  // `document`, not `window`: the event is stopped before it bubbles that far.
  //
  // Capture phase + stopImmediatePropagation, both load-bearing. Excalidraw
  // binds its own `onKeyDown` on `document` (App.tsx) and deliberately does NOT
  // bail out on Escape even inside a writable element — Escape is how it
  // finalizes the current action. A bubble-phase listener plus stopPropagation
  // would not stop it: they are siblings on the same node, and stopPropagation
  // only blocks *later* nodes on the path. Capturing on `document` runs before
  // that bubble listener, and stopImmediatePropagation is what keeps it from
  // running at all — otherwise dismissing this dialog also deselects/finalizes
  // on the canvas underneath.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      // Mid-composition Escape belongs to the IME, which uses it to dismiss the
      // candidate window. Closing on it would take a CJK user's answers with
      // it. `keyCode === 229` is the older signal for the same state and still
      // the only one some browsers set.
      if (event.isComposing || event.keyCode === 229) {
        return;
      }
      if (event.key === "Escape") {
        event.stopImmediatePropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown, { capture: true });
    return () =>
      document.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [isOpen, onClose]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        {/*
          Close on click-outside is wired here rather than on the backdrop
          itself: the backdrop sits *under* this scroll container, so it never
          receives the click. The panel stops the event so clicks inside the
          form don't dismiss it.
        */}
        {/*
          The overlay is a viewport-sized flex box that never scrolls: `p-4`
          leaves a 16px gutter and `max-h-full` keeps the panel inside it, so the
          dialog stays pinned to the screen at any window height. When the form
          is taller than the space available, the body below scrolls rather than
          the page — which also keeps the close button pinned in view.
        */}
        <div
          className="fixed inset-0 flex items-center justify-center p-4"
          onMouseDown={(event: React.MouseEvent) => {
            pressStartedOnOverlay.current =
              event.target === event.currentTarget;
          }}
          onClick={(event: React.MouseEvent) => {
            if (
              pressStartedOnOverlay.current &&
              event.target === event.currentTarget
            ) {
              onClose();
            }
            pressStartedOnOverlay.current = false;
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            {/* No `relative`: it existed only to anchor a floating X in the
                corner. Dismissal now lives in the button row as Cancel, beside
                Submit, where the other decision already is. */}
            <Dialog.Panel
              className="flex max-h-full w-full max-w-3xl transform flex-col overflow-hidden rounded-2xl bg-white [.dark_&]:bg-zinc-900 text-left shadow-xl transition-all"
              onClick={(event: React.MouseEvent) => event.stopPropagation()}
            >
              {/* Flex column so the form can shrink to fit; `overflow-y-auto`
                  is the floor, only engaging once the textareas hit their
                  min-height and there is genuinely nothing left to give. */}
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                <FeedbackPanelSwap
                  showResult={resultVisible}
                  formPanel={
                    <FeedbackFields
                      form={form}
                      initialValues={draft}
                      onDraftChange={setDraft}
                      onSubmit={handleSubmit}
                      onCancel={onClose}
                    />
                  }
                  resultPanel={
                    result?.status === "error" ? (
                      <FeedbackError
                        message={result.message}
                        // Rewinds to the form, which remounts with `draft` — so
                        // Retry costs one click, not a retyped form.
                        onRetry={() => setResultVisible(false)}
                      />
                    ) : (
                      <FeedbackThankYou onDone={onClose} />
                    )
                  }
                />
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default FeedbackDialog;
